import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { geoData } from '@fs/zion-config'
import zionDebug from '@fs/zion-debug'
import { hookUpGenericInputField, useZionFormContext } from '@fs/zion-form'
import { useController } from 'react-hook-form'
import { DEFAULT_LOCATION_NAME } from '../constants'
import PhoneNumber from '../PhoneNumber'
import type { FormPhoneNumberProps, FormPhoneNumberRef } from '../types'

const debug = zionDebug('zion:phone-number')

const FormPhoneNumber = forwardRef<FormPhoneNumberRef, FormPhoneNumberProps>(function FormPhoneNumber(
  { locationName = DEFAULT_LOCATION_NAME, defaultLocation: defaultLocationProp, ...props },
  ref
): JSX.Element {
  const { control, config } = useZionFormContext()
  const defaultLocationFromConfig = config?.defaultValues?.[locationName]
  useEffect(() => {
    if (defaultLocationFromConfig && defaultLocationProp) {
      debug(
        'defaultLocation was passed directly to the component and specified in defaultValues. Only the value from defaultValues will take effect.'
      )
    }
  }, [defaultLocationFromConfig, defaultLocationProp, locationName])

  const defaultLocation = defaultLocationFromConfig || defaultLocationProp || geoData?.country
  const {
    field: { onChange },
    // set defaultValue here but useZionForm defaultValues will always override and PhoneNumber will broadcast onLocationChange to a new default if the default is not supported
  } = useController({ name: locationName, control, defaultValue: defaultLocation })
  // onChange from useController seems to change frequently
  const onLocationChange = useCallback(
    (location) => {
      // setValue here seems insufficient to properly initialize form if defaultLocation is not supported
      // could have separate initializeDefaultLocation function to pass down
      onChange({
        type: 'change',
        target: {
          value: location,
          name: locationName,
        },
      })
    },
    [onChange, locationName]
  )
  const safeFormRef = useRef<HTMLInputElement>()
  useImperativeHandle(ref, () => ({
    // Limit what react-hook-form has access to otherwise bugs can result when react-hook-form
    // reads or writes formatted/unformatted values directly to the input (similar approach used in react-phone-number-input/react-hook-form)
    focus: () => {
      safeFormRef.current.focus()
    },
  }))

  return (
    <PhoneNumber {...props} onLocationChange={onLocationChange} defaultLocation={defaultLocation} ref={safeFormRef} />
  )
})

export default hookUpGenericInputField(FormPhoneNumber) as typeof FormPhoneNumber
